import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { RootState } from "typesafe-actions";
import {
    getPreviousTransactionActionCreator,
    nextTransactionIdActionCreator,
    setNextTransactionNullActionCreator,
} from "../DataEntryDetailsDucks";
import { clearTransactionId, createTransaction } from "../../landingPage/LandingPageDucks";

/**
 * master-demo-v6.html's <footer class="action-footer"> - a DIRECT CHILD of
 * the Quick Entry panel (sibling of .qe__header / .qe__list), NOT the
 * classic .panel-footer restyled or moved into the left transaction-summary
 * pane. Confirmed by DOM inspection: the footer sits inside
 * <main class="panel panel--v2" aria-label="Quick Entry"> and closes before
 * that panel does (Spec #34098 / F1a, AC9/AC12 revised).
 *
 * Independently connected (its own small mapDispatchToProps/mapStateToProps)
 * rather than threading 5 more action creators through DataEntryDetails.tsx's
 * already-large Props - mirrors the same pattern DataEntryDetailsLeftPane.tsx
 * and DataEntryDetailsRightPane.tsx already use for their own connect().
 */
interface OwnProps {
    readonly transactionId: string;
    readonly organizationId?: number;
    readonly requestContext?: number;
    readonly resetForm: () => void;
    readonly resetDataEntryState: () => void;
    readonly healthSystemDataEntryCount?: number;
    readonly actionCount: number;
    readonly onAction: () => void;
    /**
     * AB#35149: hard-disable the action button. Set while a confirmed Request Type/Purpose change
     * is round-tripping to the backend and possibly resetting another row - the rows on screen are
     * the OLD confirmed ones and actionCount still reads 0, so without this the button says
     * "Confirm All" and commits values that are about to be replaced.
     *
     * Distinct from actionCount > 0, which must stay CLICKABLE: that click jumps to the outstanding
     * row, which is a useful affordance rather than a blocked action.
     */
    readonly disabled?: boolean;
}

interface ReduxStateProps {
    readonly facilityId?: number;
}

interface ReduxDispatchProps {
    readonly getPreviousTransaction: (transactionId: string) => void;
    readonly getNextTransactionId: (facilityId: number, context: number) => void;
    readonly setNextTransactionIDNull: () => void;
    readonly createTransaction: (data: any) => void;
    readonly clearTransactionId: () => void;
}

type Props = OwnProps & ReduxStateProps & ReduxDispatchProps;

const QuickEntryActionFooter: React.FC<Props> = (props) => {
    const resetTransaction = () => {
        props.resetDataEntryState();
        props.clearTransactionId();
    };

    const onPreviousRequest = () => {
        resetTransaction();
        props.resetForm();
        props.getPreviousTransaction(props.transactionId);
        props.setNextTransactionIDNull();
    };

    const onNewRequest = () => {
        props.createTransaction({ facilityId: props.organizationId });
        resetTransaction();
        props.resetForm();
    };

    const onNextRequest = () => {
        if (props.facilityId) {
            props.getNextTransactionId(props.facilityId, props.requestContext ?? 1);
        }
        resetTransaction();
        props.resetForm();
    };

    return (
        <footer className="action-footer" aria-label="Queue progress and submit">
            <div className="action-footer__session">
                {props.healthSystemDataEntryCount !== undefined ? (
                    <div className="action-footer__counter">
                        <span className="action-footer__counter-label">Data Entry queue</span>
                        <span className="action-footer__counter-value">{props.healthSystemDataEntryCount}</span>
                    </div>
                ) : null}
                <div className="action-footer__session-controls">
                    <button type="button" className="action-footer__link action-footer__link--prev" onClick={onPreviousRequest} aria-label="Go to previous request">
                        <svg className="action-footer__link-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <Link to="/data">Previous Request</Link>
                    </button>
                    <button type="button" className="action-footer__link action-footer__link--new" onClick={onNewRequest} aria-label="Start a new request">
                        <svg className="action-footer__link-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        <Link to="/data">New Request</Link>
                    </button>
                    <button type="button" className="action-footer__link action-footer__link--next" onClick={onNextRequest} aria-label="Go to next request">
                        <svg className="action-footer__link-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <Link to="/data">Next Request</Link>
                    </button>
                </div>
            </div>
            <button
                type="button"
                className={`action-footer__action-btn${props.actionCount === 0 ? " is-ready" : ""}${props.disabled ? " is-disabled" : ""}`}
                disabled={props.disabled}
                aria-disabled={props.disabled}
                onClick={props.disabled ? undefined : props.onAction}
            >
                <span className="action-footer__action-icon" aria-hidden="true">
                    {props.actionCount === 0 ? (
                        <svg className="icon-check" viewBox="0 0 18 18" fill="none">
                            <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.4" fill="none" />
                            <path d="M5 9.5l2.7 2.7 5.3-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <svg className="icon-arrow" viewBox="0 0 18 18" fill="none">
                            <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.4" fill="none" />
                            <path d="M7.5 5.5L11 9l-3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </span>
                <span className="action-footer__action-label">
                    {props.actionCount === 0 ? "Confirm All" : `Next Action Required (${props.actionCount})`}
                </span>
            </button>
        </footer>
    );
};

function mapStateToProps(state: RootState): ReduxStateProps {
    return {
        facilityId: state.landingPageState.selectedFacilityDetails?.facility.id,
    };
}

function mapDispatchToProps(dispatch: Function): ReduxDispatchProps {
    return {
        getPreviousTransaction: (transactionId: string) => dispatch(getPreviousTransactionActionCreator(transactionId)),
        getNextTransactionId: (facilityId: number, context: number) => dispatch(nextTransactionIdActionCreator(facilityId, context)),
        setNextTransactionIDNull: () => dispatch(setNextTransactionNullActionCreator()),
        createTransaction: (data: any) => dispatch(createTransaction(data)),
        clearTransactionId: () => dispatch(clearTransactionId()),
    };
}

export default connect(mapStateToProps as any, mapDispatchToProps)(QuickEntryActionFooter);
